import { useMemo, useRef, useState } from 'react'
import { useMessages } from '../../../api/hooks/use.messages'
import { IMessage } from '../../../api/service/message.service'
import notificationIcon from '../../../styles/icons/notification.svg'
import useWindowWidth from '../../../utils/use.window.width'
import * as styled from './styled'

const groupMessagesByDate = (messages: IMessage[]) => {
	const groups: { [key: string]: IMessage[] } = {}

	messages.forEach(msg => {
		const dateLabel = new Date(msg.publicationDate).toLocaleDateString(
			'uk-UA',
			{
				day: '2-digit',
				month: 'long',
				year: 'numeric',
				timeZone: 'UTC',
			},
		)

		if (!groups[dateLabel]) groups[dateLabel] = []
		groups[dateLabel].push(msg)
	})

	return Object.entries(groups).map(([date, notifications]) => ({
		date,
		notifications,
	}))
}

const formatTimeSafe = (dateStr: string) => {
	if (!dateStr) return '--:--'

	const parts = dateStr.split(' ')
	if (parts.length === 2) {
		const timePart = parts[1]
		const [hours, minutes] = timePart.split(':')
		return `${hours}:${minutes}`
	}

	return new Date(dateStr).toLocaleTimeString('uk-UA', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
		timeZone: 'UTC',
	})
}


export const NotificationsComponent = () => {
	const [isOpen, setIsOpen] = useState(false)
	const scrollRef = useRef<HTMLDivElement>(null)
	
	const { data: rawMessages } = useMessages()

	const messages: IMessage[] = useMemo(() => {
		if (!rawMessages) return []
		if (Array.isArray(rawMessages)) return rawMessages
		if (Array.isArray((rawMessages as any)?.data))
			return (rawMessages as any).data
		return []
	}, [rawMessages])

	const windowWidth = useWindowWidth()
	const isMobile = windowWidth <= 768

	const groupedData = useMemo(() => {
		return messages.length > 0 ? groupMessagesByDate(messages) : []
	}, [messages])

	const totalCount = messages.length
	const hasMore = totalCount >= 1
	const firstNotification = groupedData[0]?.notifications[0]

	const handleOpen = (val: boolean) => {
		setIsOpen(val)
	}

	const scrollToTop = () => {
		scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
	}

	return (
		<>
			{rawMessages && (
				<>
					{isMobile && isOpen && (
						<styled.Overlay onClick={() => handleOpen(false)} />
					)}

					<styled.NotificationsWrapper
						$isExpanded={isOpen}
						$isMobile={isMobile}
					>
						<styled.ScrollArea $isExpanded={isOpen} ref={scrollRef}>
							{!isOpen && firstNotification && (
								<styled.NotificationCard>
									<styled.CardHeader $isExpanded={isOpen}>
										<img src={notificationIcon} alt='icon' />
										<styled.CardMeta>
											<styled.CardTitle $isExpanded={isOpen}>
												{firstNotification.title}
											</styled.CardTitle>
											<styled.CardTime>
												{formatTimeSafe(firstNotification.publicationDate)}
											</styled.CardTime>
										</styled.CardMeta>
									</styled.CardHeader>
								</styled.NotificationCard>
							)}
							{isOpen &&
								groupedData.map(group => (
									<styled.DateGroup key={group.date}>
										<styled.DateLabel>{group.date}</styled.DateLabel>
										{group.notifications.map(notification => (
											<styled.NotificationCard key={notification.id}>
												<styled.CardHeader>
													<img src={notificationIcon} alt='icon' />
													<styled.CardMeta>
														<styled.CardTitle $isExpanded={isOpen}>
															{notification.title}
														</styled.CardTitle>
														<styled.CardTime>
															{formatTimeSafe(notification.publicationDate)}
														</styled.CardTime>
													</styled.CardMeta>
												</styled.CardHeader>
												<styled.CardText
													dangerouslySetInnerHTML={{
														__html: notification.textHtml,
													}}
												/>
											</styled.NotificationCard>
										))}
									</styled.DateGroup>
								))}
						</styled.ScrollArea>

						{hasMore && (
							<styled.ExpandBtn
								$isExpanded={isOpen}
								onClick={() => {
									if (isOpen) scrollToTop()
									handleOpen(!isOpen)         
								}}
							>
								↑
							</styled.ExpandBtn>
						)}
					</styled.NotificationsWrapper>
				</>
			)}
		</>
	)
}


